import { DiagnosticId } from '@/diagnostic/domain/DiagnosticId.ts';
import { Amount } from '@/diagnostic/domain/Amount.ts';
import { Duration } from '@/diagnostic/domain/Duration.ts';
import { ApiName } from '@/diagnostic/domain/ApiName.ts';
import { Optional } from '@/generic/functional';
import { DateTime } from '@/diagnostic/domain/DateTime.ts';

type DiagnosticSummaryBuilder = {
  id: DiagnosticId | string;
  name: Optional<ApiName | string>;
  duration: Duration | number;
  cost: Amount | number;
  date: DateTime | string;
  linkedToOrganization: boolean;
};

const toDateTime = (date: DateTime | string): DateTime => {
  if (date instanceof DateTime) {
    return date;
  }
  return DateTime.fromISO(date).orThrow();
};

export class DiagnosticSummary {
  readonly #id: DiagnosticId;
  readonly #name: Optional<ApiName>;
  readonly #duration: Duration;
  readonly #cost: Amount;
  readonly #date: DateTime;
  readonly #linkedToOrganization: boolean;

  private constructor(builder: DiagnosticSummaryBuilder) {
    this.#id = DiagnosticId.of(builder.id);
    this.#name = builder.name.map((name) => ApiName.of(name));
    this.#duration = Duration.fromSeconds(builder.duration);
    this.#cost = Amount.of(builder.cost);
    this.#date = toDateTime(builder.date);
    this.#linkedToOrganization = builder.linkedToOrganization;
  }

  static of(builder: DiagnosticSummaryBuilder): DiagnosticSummary {
    return new DiagnosticSummary(builder);
  }

  get id(): DiagnosticId {
    return this.#id;
  }

  get name(): Optional<ApiName> {
    return this.#name;
  }

  get duration(): Duration {
    return this.#duration;
  }

  get cost(): Amount {
    return this.#cost;
  }

  get date(): DateTime {
    return this.#date;
  }

  get linkedToOrganization(): boolean {
    return this.#linkedToOrganization;
  }
}
